import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Compass,
  Palette,
  Sparkles,
  Type,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Υπηρεσίες Branding';
const serviceSlug = 'branding';

type Locale = 'en' | 'el';

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
};

type SectionCopy = {
  eyebrow?: string;
  heading: string;
  description: string;
};

type BulletSection = SectionCopy & {
  rightTitle: string;
  bullets: string[];
  note: string;
};

type CapabilityGroup = {
  title: string;
  description: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  bullets: string[];
};

type DeliverablesContent = {
  eyebrow: string;
  heading: string;
  description: string;
  bullets: string[];
  note: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

type ClosingContent = {
  eyebrow?: string;
  title: string;
  description: string;
  bullets: string[];
  primaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'Branding services',
    title: 'Branding that clarifies who you are and why people should choose you.',
    description:
      "Branding isn't just a logo. It's how people understand you, remember you, and trust you. We help businesses build a clear identity, the right message, and a consistent presence across every touchpoint.",
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See branding work',
  },
  el: {
    eyebrow: 'Υπηρεσίες Branding',
    title: 'Branding που ξεκαθαρίζει ποιοι είστε και γιατί να σας επιλέξουν.',
    description:
      'Το branding δεν είναι μόνο λογότυπο. Είναι το πώς σας καταλαβαίνει ο κόσμος, πώς σας θυμάται και πώς σας εμπιστεύεται. Βοηθάμε επιχειρήσεις να αποκτήσουν καθαρή εικόνα, σωστό μήνυμα και συνεπή παρουσία σε όλα τα σημεία επαφής.',
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα branding',
  },
};

const brandingMeaningCopy: Record<Locale, BulletSection> = {
  en: {
    eyebrow: 'Branding in practice',
    heading: 'What branding means in practice',
    description: 'For us, branding means:',
    rightTitle: 'In practice',
    bullets: [
      'Being clear about what you do',
      'Helping customers see if you are the right fit',
      'Consistency across site, social, print, and communication',
    ],
    note: "We don't make “pretty things”. We build identity that makes sense.",
  },
  el: {
    eyebrow: 'Τι είναι το branding στην πράξη',
    heading: 'Τι είναι το branding στην πράξη',
    description: 'Για εμάς, branding σημαίνει:',
    rightTitle: 'Στην πράξη',
    bullets: [
      'να είναι ξεκάθαρο τι κάνετε',
      'να καταλαβαίνει ο πελάτης αν του ταιριάζετε',
      'να υπάρχει συνέπεια σε site, social, έντυπα και επικοινωνία',
    ],
    note: 'Δεν φτιάχνουμε «όμορφα πράγματα». Φτιάχνουμε εικόνα που βγάζει νόημα.',
  },
};

const capabilitiesCopy: Record<Locale, { eyebrow: string; heading: string; description: string; groups: CapabilityGroup[] }> = {
  en: {
    eyebrow: 'What we handle',
    heading: 'What we handle',
    description: 'Clear scope with the key components of a brand system.',
    groups: [
      {
        title: 'Brand strategy',
        description: 'We organize the core position before design begins.',
        bullets: [
          'What you offer',
          'Who you serve',
          'What sets you apart from competitors',
          'A clear message, without vague claims',
        ],
      },
      {
        title: 'Verbal identity',
        description: 'We define how your business sounds.',
        bullets: [
          'Tone of voice',
          'Key messages',
          'What you say and what you avoid',
        ],
      },
      {
        title: 'Visual identity',
        description: 'We design the core visual system.',
        bullets: [
          'Logo design or refresh',
          'Colors and typography',
          'Essential usage rules',
          'Consistency everywhere, every time',
        ],
      },
      {
        title: 'Everyday assets',
        description: "We don't leave you with a PDF in a drawer.",
        bullets: [
          'Social templates',
          'Key layouts for site or presentations',
          'Usage guidelines for the team',
        ],
      },
    ],
  },
  el: {
    eyebrow: 'Τι αναλαμβάνουμε',
    heading: 'Τι αναλαμβάνουμε',
    description: 'Ξεκάθαρο scope για τα βασικά στοιχεία μιας σωστής ταυτότητας.',
    groups: [
      {
        title: 'Στρατηγική εικόνας',
        description: 'Βάζουμε σε τάξη:',
        bullets: [
          'τι προσφέρετε',
          'σε ποιον απευθύνεστε',
          'τι σας ξεχωρίζει από τον ανταγωνισμό',
          'Καταλήγουμε σε ξεκάθαρο μήνυμα, χωρίς γενικόλογα.',
        ],
      },
      {
        title: 'Λεκτική ταυτότητα',
        description: 'Ορίζουμε πώς «μιλάει» η επιχείρησή σας:',
        bullets: [
          'τόνος επικοινωνίας',
          'βασικά μηνύματα',
          'τι λέτε και τι αποφεύγετε',
          'Ώστε όλα τα κείμενα να έχουν την ίδια φωνή.',
        ],
      },
      {
        title: 'Οπτική ταυτότητα',
        description: 'Σχεδιάζουμε:',
        bullets: [
          'λογότυπο ή ανανέωση υπάρχοντος',
          'χρώματα και γραμματοσειρές',
          'βασικούς κανόνες χρήσης',
          'Για να δείχνετε ίδιοι παντού, όχι διαφορετικοί κάθε φορά.',
        ],
      },
      {
        title: 'Υλικό για καθημερινή χρήση',
        description: 'Δεν σας αφήνουμε με ένα PDF στο συρτάρι.',
        bullets: [
          'πρότυπα για social',
          'βασικά layouts για site ή παρουσιάσεις',
          'οδηγίες για σωστή χρήση',
        ],
      },
    ],
  },
};

const processIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'How we work',
    heading: 'How we work',
    description: 'A simple and clear model.',
  },
  el: {
    eyebrow: 'Πώς δουλεύουμε',
    heading: 'Πώς δουλεύουμε',
    description: 'Ένα απλό και ξεκάθαρο μοντέλο',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Understanding',
      description: 'We talk with you and learn:',
      bullets: ['Your business', 'Your audience', 'What you want to achieve'],
    },
    {
      title: 'Direction',
      description: 'We define:',
      bullets: ['The core message', 'Your market position', 'The overall identity'],
    },
    {
      title: 'Design',
      description: 'We work on:',
      bullets: ['Verbal and visual identity', 'Example applications', 'Revisions together with you'],
    },
    {
      title: 'Delivery & use',
      description: 'We provide everything you need to continue correctly.',
      bullets: ['Guidelines and assets', 'Templates for daily use', 'Structured handover'],
    },
  ],
  el: [
    {
      title: 'Κατανόηση',
      description: 'Μιλάμε μαζί σας και μαθαίνουμε:',
      bullets: ['την επιχείρησή σας', 'το κοινό σας', 'τι θέλετε να πετύχετε'],
    },
    {
      title: 'Κατεύθυνση',
      description: 'Ορίζουμε:',
      bullets: ['το βασικό μήνυμα', 'τη θέση σας στην αγορά', 'τη συνολική εικόνα'],
    },
    {
      title: 'Σχεδιασμός',
      description: 'Δουλεύουμε πάνω σε:',
      bullets: ['λεκτική και οπτική ταυτότητα', 'παραδείγματα εφαρμογής', 'διορθώσεις μαζί σας'],
    },
    {
      title: 'Παράδοση & χρήση',
      description: 'Σας δίνουμε όλα όσα χρειάζεστε για να συνεχίσετε σωστά.',
      bullets: ['Οδηγίες και assets', 'Πρότυπα για καθημερινή χρήση', 'Οργανωμένη παράδοση'],
    },
  ],
};

const deliverablesCopy: Record<Locale, DeliverablesContent> = {
  en: {
    eyebrow: 'Deliverables',
    heading: 'What we deliver',
    description: 'Everything delivered in a format you can use immediately.',
    bullets: [
      'Clear branding strategy',
      'Visual and verbal identity',
      'Usage guidelines',
      'Practical templates',
      'Assets ready to use',
    ],
    note: 'All provided in a form you can apply right away.',
  },
  el: {
    eyebrow: 'Τι παραδίδουμε',
    heading: 'Τι παραδίδουμε',
    description: 'Όλα σε μορφή που μπορεί να χρησιμοποιηθεί άμεσα.',
    bullets: [
      'Καθαρή στρατηγική branding',
      'Οπτική και λεκτική ταυτότητα',
      'Οδηγίες χρήσης',
      'Πρακτικά templates',
      'Υλικό έτοιμο για εφαρμογή',
    ],
    note: 'Όλα σε μορφή που μπορεί να χρησιμοποιηθεί άμεσα.',
  },
};

const faqIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    description: 'Everything you need to know before starting.',
  },
  el: {
    eyebrow: 'Συχνές Ερωτήσεις',
    heading: 'Συχνές Ερωτήσεις',
    description: 'Όσα χρειάζεται να γνωρίζετε πριν ξεκινήσετε.',
  },
};

const faqCopy: Record<Locale, FaqItem[]> = {
  en: [
    {
      question: 'Can you improve existing branding?',
      answer: 'Yes. Very often we build on what already exists.',
    },
    {
      question: 'Is branding only for large companies?',
      answer: 'No. Branding is even more critical for small businesses that want to stand out.',
    },
    {
      question: 'Will I be able to use it on my own afterwards?',
      answer: 'Yes. Our goal is for you to know what you are doing and why.',
    },
    {
      question: 'Can it be combined with website or marketing?',
      answer: 'Yes. And it usually performs better when combined.',
    },
  ],
  el: [
    {
      question: 'Μπορείτε να βελτιώσετε υπάρχον branding;',
      answer: 'Ναι. Πολύ συχνά δουλεύουμε πάνω σε αυτό που ήδη υπάρχει.',
    },
    {
      question: 'Είναι μόνο για μεγάλες εταιρείες;',
      answer: 'Όχι. Το branding είναι πιο κρίσιμο για μικρές επιχειρήσεις που θέλουν να ξεχωρίσουν.',
    },
    {
      question: 'Θα μπορώ να το χρησιμοποιώ μόνος μου μετά;',
      answer: 'Ναι. Στόχος μας είναι να ξέρετε τι κάνετε και γιατί.',
    },
    {
      question: 'Συνδυάζεται με website ή marketing;',
      answer: 'Ναι. Και συνήθως έχει καλύτερα αποτελέσματα όταν συνδυάζεται.',
    },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Next step',
    title: 'Want an identity that represents you correctly?',
    description: 'Let’s look at:',
    bullets: [
      'Where you are today',
      'What needs to change',
      'What the right next step is',
    ],
    primaryCta: 'Book a free consultation',
  },
  el: {
    eyebrow: 'Επόμενο βήμα',
    title: 'Θέλετε μια εικόνα που να σας εκπροσωπεί σωστά;',
    description: 'Ελάτε να δούμε:',
    bullets: [
      'πού βρίσκεστε σήμερα',
      'τι χρειάζεται να αλλάξει',
      'ποιο είναι το σωστό επόμενο βήμα',
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
  },
};

const capabilityIcons: LucideIcon[] = [Compass, Type, Palette, Sparkles];

export default function Branding() {
  const { language } = useLanguage();
  const location = useLocation();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();

  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'Branding Services' : serviceName;

  const hero = heroCopy[locale];
  const brandingMeaning = brandingMeaningCopy[locale];
  const capabilities = capabilitiesCopy[locale];
  const processIntro = processIntroCopy[locale];
  const processSteps = processCopy[locale];
  const deliverables = deliverablesCopy[locale];
  const faqIntro = faqIntroCopy[locale];
  const faqItems = faqCopy[locale];
  const closing = closingCopy[locale];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'Branding')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.title}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href={localizedPath('/projects')}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1.05fr)] lg:items-start lg:gap-12">
                <div className="space-y-4">
                  {brandingMeaning.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                      {brandingMeaning.eyebrow}
                    </p>
                  ) : null}
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{brandingMeaning.heading}</h2>
                  <p className="text-lg text-white/80">{brandingMeaning.description}</p>
                </div>

                <div className="hidden lg:block h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />

                <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-6 sm:p-7 shadow-glass">
                  <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-24 -left-16 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative space-y-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
                      {brandingMeaning.rightTitle}
                    </p>
                    <ul className="space-y-4 text-sm text-white/70">
                      {brandingMeaning.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-brand-highlight/80" />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-white/55">{brandingMeaning.note}</p>
                  </div>
                </Card>
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {capabilities.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{capabilities.heading}</h2>
                <p className="text-lg text-white/70">{capabilities.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {capabilities.groups.map((group, index) => {
                  const Icon = capabilityIcons[index] ?? Compass;
                  return (
                    <Card
                      key={group.title}
                      className="group space-y-4 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-2xl font-semibold text-white">{group.title}</h3>
                      <p className="text-sm text-white/70">{group.description}</p>
                      <ul className="space-y-3 text-sm text-white/75">
                        {group.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                {processIntro.eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {processIntro.eyebrow}
                  </p>
                ) : null}
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntro.heading}</h2>
                <p className="text-lg text-white/70">{processIntro.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3 className="text-2xl font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                      {step.bullets.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                <div className="relative space-y-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {deliverables.eyebrow}
                  </p>
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{deliverables.heading}</h2>
                  <p className="text-lg text-white/70">{deliverables.description}</p>
                  <ul className="space-y-3 text-sm text-white/75">
                    {deliverables.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-white/55">{deliverables.note}</p>
                </div>
              </Card>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {faqIntro.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-5xl">{faqIntro.heading}</h2>
                <p className="mt-4 text-lg text-white/70">{faqIntro.description}</p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative text-center">
                  {closing.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {closing.eyebrow}
                    </p>
                  ) : null}
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {closing.title}
                  </Display>
                  <p className="mx-auto mt-4 max-w-3xl text-center text-lg text-white/70">
                    {closing.description}
                  </p>
                  <ul className="mx-auto mt-6 grid max-w-3xl gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
                    {closing.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                      >
                        <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => handleCTA(closing.primaryCta)}
                    >
                      {closing.primaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
